import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Plus, Trash2 } from "lucide-react";
import { sb } from "@/lib/sb";

export const Route = createFileRoute("/_authenticated/dashboard/services")({
  component: ServicesAdmin,
});

type Service = {
  id: string;
  slug: string;
  title: string;
  intro: string | null;
  sort_order: number | null;
};

const slugify = (s: string) =>
  s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);

function ServicesAdmin() {
  const [services, setServices] = useState<Service[]>([]);
  const [adding, setAdding] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newIntro, setNewIntro] = useState("");

  const load = async () => {
    const { data, error } = await sb
      .from("service_settings")
      .select("id, slug, title, intro, sort_order")
      .order("sort_order", { ascending: true });
    if (error) {
      toast.error(error.message);
      return;
    }
    setServices((data as Service[]) ?? []);
  };
  useEffect(() => {
    load();
  }, []);

  const save = async (svc: Service) => {
    const { error } = await sb
      .from("service_settings")
      .update({
        title: svc.title,
        intro: svc.intro,
        sort_order: svc.sort_order,
      })
      .eq("id", svc.id);
    if (error) toast.error(error.message);
    else toast.success("Saved");
  };

  const addService = async () => {
    const title = newTitle.trim();
    if (!title) {
      toast.error("Title required");
      return;
    }
    const base = slugify(title) || `service-${Date.now()}`;
    let slug = base;
    let n = 1;
    while (services.some((s) => s.slug === slug)) slug = `${base}-${n++}`;
    const sort_order = services.reduce((m, s) => Math.max(m, s.sort_order ?? 0), 0) + 1;
    const { error } = await sb
      .from("service_settings")
      .insert({ slug, title, intro: newIntro.trim() || null, sort_order });
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Service added");
    setAdding(false);
    setNewTitle("");
    setNewIntro("");
    load();
  };

  const deleteService = async (svc: Service) => {
    const { count, error: countErr } = await sb
      .from("projects")
      .select("id", { count: "exact", head: true })
      .eq("category", svc.title);
    if (countErr) {
      toast.error(countErr.message);
      return;
    }
    if ((count ?? 0) > 0) {
      toast.error(
        `Can't delete "${svc.title}" — ${count} project${count === 1 ? " is" : "s are"} attached to this category.`,
      );
      return;
    }
    if (!confirm("Delete this service?")) return;
    const { error } = await sb.from("service_settings").delete().eq("id", svc.id);
    if (error) toast.error(error.message);
    else {
      toast.success("Deleted");
      load();
    }
  };

  const patch = (id: string, fields: Partial<Service>) =>
    setServices((p) => p.map((x) => (x.id === id ? { ...x, ...fields } : x)));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl font-bold text-ink">Services</h1>
        <button
          onClick={() => setAdding(true)}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-brand hover:bg-brand-hover text-primary-foreground text-sm font-semibold"
        >
          <Plus size={16} /> Add Service
        </button>
      </div>

      {services.map((s) => (
        <div key={s.id} className="bg-card border border-border rounded-xl p-4 space-y-2">
          <input
            value={s.title}
            onChange={(e) => patch(s.id, { title: e.target.value })}
            className="w-full px-3 py-2 rounded-lg bg-background border border-border font-semibold"
          />
          <textarea
            rows={2}
            value={s.intro ?? ""}
            onChange={(e) => patch(s.id, { intro: e.target.value })}
            className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm resize-none"
          />
          <div className="flex items-center gap-2 flex-wrap">
            <label className="text-xs text-muted-foreground">Order</label>
            <input
              type="number"
              value={s.sort_order ?? 0}
              onChange={(e) => patch(s.id, { sort_order: Number(e.target.value) })}
              className="w-20 px-2 py-1.5 rounded-lg bg-background border border-border text-sm"
            />
            <button
              onClick={() => save(s)}
              className="px-4 py-1.5 rounded-lg bg-brand hover:bg-brand-hover text-primary-foreground text-sm font-semibold"
            >
              Save
            </button>
            <button
              onClick={() => deleteService(s)}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-destructive/40 text-destructive text-sm hover:bg-destructive/10"
            >
              <Trash2 size={14} /> Delete
            </button>
            <span className="text-xs text-muted-foreground ml-auto">slug: {s.slug}</span>
          </div>
        </div>
      ))}

      {adding && (
        <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-start justify-center p-4 overflow-auto">
          <div className="bg-card border border-border rounded-2xl p-5 max-w-md w-full my-8 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="font-display font-bold text-ink">Add Service</h2>
              <button onClick={() => setAdding(false)}>
                <X size={18} />
              </button>
            </div>
            <input
              autoFocus
              placeholder="Title (e.g. Roofing)"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-background border border-border"
            />
            <textarea
              rows={3}
              placeholder="Short intro"
              value={newIntro}
              onChange={(e) => setNewIntro(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm resize-none"
            />
            <p className="text-xs text-muted-foreground">
              Slug: {slugify(newTitle) || "—"} (set once, cannot be changed later)
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setAdding(false)}
                className="px-4 py-1.5 rounded-lg border border-border text-sm"
              >
                Cancel
              </button>
              <button
                onClick={addService}
                className="px-4 py-1.5 rounded-lg bg-brand hover:bg-brand-hover text-primary-foreground text-sm font-semibold"
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
